import { type FC, useCallback } from "react";
import { Colors, Input } from "@snapchat/snap-design-system";
import { css } from "@emotion/css";
import { Flexbox } from "../../core/components";
import { Divider, NumberInput, InfoTooltip } from "../../common/components";
import { VendorDataCollection } from "../../vendor-data/models";
import { VendorDataSection } from "../../vendor-data/components";
import { type VendorDataChangedAction } from "../../vendor-data";
import { ScheduleDateRange } from "../../groups/group-details/components/ScheduleDateRange";
import { RangeModifier } from "../../connections/models";
import { type Permissions } from "../../gql-generated/aliases";
import { LensModel } from "../lens-details/models";

const inputStyle = css`
    margin-right: 40px;

    & .sds-input-base.read-only {
        color: ${Colors.Black.V200};
    }

    :not(:last-child) {
        margin-bottom: 16px;
    }
`;

interface GroupInfoSectionProps {
    groupId: string;
    groupName: string;
    priority: number;
    vendorData: VendorDataCollection;
    scheduleRange: RangeModifier;
    numberOfLenses: number;
    onScheduleChanged: (scheduleRange: RangeModifier, groupId: string) => void;
    onVendorDataChanged: (event: VendorDataChangedAction, groupId: string) => void;
    permissions: Permissions;
    lens: LensModel;
}

export const GroupInfo: FC<GroupInfoSectionProps> = ({
    groupId,
    groupName,
    priority,
    vendorData,
    scheduleRange,
    numberOfLenses,
    onVendorDataChanged,
    onScheduleChanged,
    permissions,
    lens: { hasMusic },
}) => {
    const changeVendorData = useCallback(
        (event: VendorDataChangedAction) => {
            onVendorDataChanged(event, groupId);
        },
        [groupId, onVendorDataChanged],
    );

    const changeSchedule = useCallback(
        (range: RangeModifier) => {
            onScheduleChanged(range, groupId);
        },
        [groupId, onScheduleChanged],
    );

    return (
        <Flexbox justifyContent="space-between">
            <Flexbox flex="1" flexDirection="column">
                <Input label="Lens Group Name" value={groupName} className={inputStyle} readOnly={true} />
                <Input label="Lens Group ID" value={groupId} className={inputStyle} readOnly={true} />
                <NumberInput label="Number of Lenses" value={numberOfLenses} className={inputStyle} readOnly={true} />
                <Input
                    label={
                        <Flexbox>
                            Priority
                            <InfoTooltip content="Lens order within Group" />
                        </Flexbox>
                    }
                    value={priority.toString()}
                    className={inputStyle}
                    readOnly={true}
                />
                <ScheduleDateRange
                    placement="top"
                    hasPermission={permissions.scheduleLenses!}
                    hasMusic={hasMusic}
                    isDisabled={!permissions.scheduleLenses || hasMusic}
                    range={scheduleRange}
                    onChange={changeSchedule}
                />
            </Flexbox>
            <Divider />
            <Flexbox flex="1" flexDirection="column">
                <VendorDataSection
                    placement="top"
                    hasPermission={permissions.updateLenses!}
                    disabled={!permissions.updateLenses}
                    vendorDataCollection={vendorData}
                    onVendorDataChanged={changeVendorData}
                    centerAddButton={true}
                />
            </Flexbox>
        </Flexbox>
    );
};
